import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { FormattedMessage } from "gatsby-plugin-react-intl"
import { Swiper, SwiperSlide } from "swiper/react"
import { A11y, Grid, Navigation, Pagination } from "swiper/modules"
import { useMedia } from "react-use"
import "swiper/css"
import "swiper/css/a11y"
import "swiper/css/navigation"
import "swiper/css/pagination"

import { Container } from "../Grid"
import Button from "../Button"
import ButtonRow from "../FormFields/ButtonRow"
import Link from "../Link"
import Snippet from "../Snippet"

import ArrowRightSVG from "../../images/icons/arrow-right.inline.svg"
import { breakpoint, media, mixins } from "../../styles"

const Wrapper = styled.div`
  background: var(--color-greyLight);
  padding: 50px 0 60px;

  @media ${media.md} {
    padding: 65px 10px;
  }

  @media ${media.lg} {
    padding: 70px 0 90px;
  }

  .swiper {
    padding-bottom: 50px;
    margin-bottom: 40px;
  }

  .swiper-slide {
    padding: 0 15px 30px;

    @media ${media.lg} {
      padding: 0 10px 30px;
    }
  }

  .swiper-button-disabled {
    display: none;
  }

  .swiper-button-prev,
  .swiper-button-next,
  .swiper-button-disable {
    @media ${media.mdDown} {
      display: none;
    }
  }
`

const Header = styled.div`
  margin: 0 0 30px;
  text-align: center;

  @media ${media.md} {
    margin: 0 0 40px;
  }

  @media ${media.lg} {
    margin-bottom: 50px;
  }

  @media ${media.xl} {
    margin-bottom: 75px;
  }

  h2 {
    ${mixins.title3}
  }
`

const DepartmentLink = styled(Link)`
  ${mixins.title6}
  background: var(--color-white);
  border-radius: 24px;
  color: var(--color-spaceblue);
  display: block;
  line-height: 1.2;
  outline-offset: -2px !important;
  text-decoration: none;

  @media ${media.xl} {
    line-height: 1.1;
  }

  > span {
    align-items: center;
    display: flex;
    height: 76px;
    overflow: hidden;
    padding: 5px 30px;

    > span {
      margin-right: 10px;
    }
  }

  svg {
    color: var(--color-sun);
    margin: 0 0 0 auto;
  }
`

const NoOpenings = styled.p`
  ${mixins.copy4}
  text-align: center;
`

const JobSlider = ({ departments }) => {
  const prefersReducedMotion = useMedia(
    "(prefers-reduced-motion: reduce)",
    false
  )

  const sliderParams = {
    modules: [A11y, Grid, Navigation, Pagination],
    a11y: {
      enabled: true,
    },
    navigation: true,
    pagination: {
      type: "bullets",
      clickable: true,
    },
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerGroup: 1,
    speed: prefersReducedMotion ? 0 : 300,
    grid: {
      fill: "row",
      rows: 3,
    },
    breakpoints: {
      [breakpoint.sm]: {
        grid: {
          fill: "row",
          rows: 3,
        },
        slidesPerView: 2,
        slidesPerGroup: 1,
      },
      [breakpoint.md]: {
        grid: {
          fill: "row",
          rows: 3,
        },
        slidesPerView: 2,
        slidesPerGroup: 1,
      },
      [breakpoint.lg]: {
        grid: {
          fill: "row",
          rows: 3,
        },
        slidesPerView: 3,
        slidesPerGroup: 3,
      },
      [breakpoint.xl]: {
        grid: {
          fill: "row",
          rows: 3,
        },
        slidesPerView: 3,
        slidesPerGroup: 3,
        slidesOffsetAfter: 120,
        slidesOffsetBefore: 120,
        width: 1200,
      },
    },
  }

  // Prefetch Snippets to prevent runtime hooks error
  const buttonLink = Snippet({ id: "careersJobsSlider", field: "linkUrl" })
  const buttonText = Snippet({ id: "careersJobsSlider", field: "linkText" })

  return (
    <Container>
      <Wrapper id="jobs" name="jobs">
        <Header>
          <h2>
            <Snippet id="careersJobsSlider" field="header" />
          </h2>
        </Header>
        {departments?.length > 0 ? (
          <>
            <Swiper {...sliderParams}>
              {departments.map((department, i) => (
                <SwiperSlide key={i}>
                  <DepartmentLink
                    to={`/careers/jobs/?department=${department.name}`}
                  >
                    <span>{department.name}</span>
                    <ArrowRightSVG aria-hidden="true" />
                  </DepartmentLink>
                </SwiperSlide>
              ))}
            </Swiper>
            <ButtonRow>
              <Button arrow as={Link} to={buttonLink} intl={false}>
                {buttonText}
              </Button>
            </ButtonRow>
          </>
        ) : (
          <NoOpenings>
            <FormattedMessage id="careers.noJobs" />
          </NoOpenings>
        )}
      </Wrapper>
    </Container>
  )
}

JobSlider.propTypes = {
  departments: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default JobSlider
